// istanbul ignore file
import { Divider } from "~/components/Divider";
import {
  anchorAttributesToLinkProps,
  iframeAttributesToLinkProps,
  styleAttributeToProps,
  TagConverter,
  TagToReactConverters,
} from "~/lib/shared/html-utils/tag-to-react-converters";
import { Headline, Link } from "~/ui-library";

export const landingPageTagToReactConverters: TagToReactConverters = new Map<
  keyof JSX.IntrinsicElements,
  TagConverter
>([
  [
    "a",
    (node, children, tagToClassNameMap, key) => (
      <Link
        {...anchorAttributesToLinkProps(node)}
        className={tagToClassNameMap.get("a")}
        key={key}
        appearance="underline"
      >
        {children}
      </Link>
    ),
  ],
  [
    "b",
    (node, children, tagToClassNameMap, key) => (
      <strong className={tagToClassNameMap.get("b")} key={key}>
        {children}
      </strong>
    ),
  ],
  [
    "br",
    (node, children, tagToClassNameMap, key) => (
      <br className={tagToClassNameMap.get("br")} key={key} />
    ),
  ],
  [
    "div",
    (node, children, tagToClassNameMap, key) => (
      <div
        className={tagToClassNameMap.get("div")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </div>
    ),
  ],
  [
    "em",
    (node, children, tagToClassNameMap, key) => (
      <em className={tagToClassNameMap.get("em")} key={key}>
        {children}
      </em>
    ),
  ],
  [
    "h1",
    (node, children, tagToClassNameMap, key) => (
      <Headline
        level={1}
        as="h1"
        className={tagToClassNameMap.get("h1")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </Headline>
    ),
  ],
  [
    "h2",
    (node, children, tagToClassNameMap, key) => (
      <Headline
        level={0}
        as="h2"
        className={tagToClassNameMap.get("h2")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </Headline>
    ),
  ],
  [
    "h3",
    (node, children, tagToClassNameMap, key) => (
      <Headline
        level={2}
        as="h3"
        className={tagToClassNameMap.get("h3")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </Headline>
    ),
  ],
  [
    "h4",
    (node, children, tagToClassNameMap, key) => (
      <Headline
        level={3}
        as="div"
        className={tagToClassNameMap.get("h4")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </Headline>
    ),
  ],
  [
    "h5",
    (node, children, tagToClassNameMap, key) => (
      <Headline
        level={5}
        as="div"
        className={tagToClassNameMap.get("h5")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </Headline>
    ),
  ],
  [
    "h6",
    (node, children, tagToClassNameMap, key) => (
      <Headline
        level={5}
        as="div"
        blockCase
        className={tagToClassNameMap.get("h6")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </Headline>
    ),
  ],
  [
    "hr",
    (node, children, tagToClassNameMap, key) => (
      <Divider containerClassName={tagToClassNameMap.get("hr")} key={key} />
    ),
  ],
  [
    "i",
    (node, children, tagToClassNameMap, key) => (
      <i className={tagToClassNameMap.get("i")} key={key}>
        {children}
      </i>
    ),
  ],
  [
    "li",
    (node, children, tagToClassNameMap, key) => (
      <li className={tagToClassNameMap.get("li")} key={key}>
        {children}
      </li>
    ),
  ],
  [
    "ol",
    (node, children, tagToClassNameMap, key) => (
      <ol className={tagToClassNameMap.get("ol")} key={key}>
        {children}
      </ol>
    ),
  ],
  [
    "p",
    (node, children, tagToClassNameMap, key) => (
      <p
        className={tagToClassNameMap.get("p")}
        key={key}
        {...styleAttributeToProps(node)}
      >
        {children}
      </p>
    ),
  ],
  [
    "u",
    (node, children, tagToClassNameMap, key) => (
      <span className={(tagToClassNameMap.get("u"), "underline")} key={key}>
        {children}
      </span>
    ),
  ],
  [
    "span",
    (...args) => {
      const [node, children, tagToClassNameMap, key] = args;

      return (
        <span
          className={tagToClassNameMap.get("span")}
          key={key}
          {...styleAttributeToProps(node)}
        >
          {children}
        </span>
      );
    },
  ],
  [
    "strong",
    (node, children, tagToClassNameMap, key) => (
      <strong className={tagToClassNameMap.get("strong")} key={key}>
        {children}
      </strong>
    ),
  ],
  [
    "ul",
    (node, children, tagToClassNameMap, key) => (
      <ul className={tagToClassNameMap.get("ul")} key={key}>
        {children}
      </ul>
    ),
  ],
  [
    "iframe",
    (node, children, tagToClassNameMap, key) => (
      <div className="relative h-0 w-full pb-[56.25%]">
        <iframe
          title="iframe"
          {...iframeAttributesToLinkProps(node)}
          className={tagToClassNameMap.get("iframe")}
          key={key}
          allowFullScreen
        />
      </div>
    ),
  ],
]);
